/**
 * @fileoverview Rule-based client status and signals for the trainer client page.
 * @description Pure functions over data the page already loads. A signal is only
 * produced when the loaded facts prove it; unknown values never become signals.
 * @module lib/client-signals
 */

import type { TrainerClientDetails } from '../queries/useTrainerClientDetails';
import type { LiftRecord, ProgressSnapshot } from '../queries/useTrainerClientProgress';

type RecentWorkout = TrainerClientDetails['recent_workouts'][number];
type ActiveProgram = TrainerClientDetails['active_program'];

/** Days without a completed workout before it becomes a signal. */
export const INACTIVE_DAYS = 7;

export interface ClientAttentionInput {
  hasProgram: boolean;
  consecutiveMissed: number | null;
  complianceRate: number | null;
}

/**
 * The client list's Needs attention rule. Shared so the list and the detail page
 * always agree. Returns the reason, or null when the client is on track.
 */
export function clientAttentionReason(input: ClientAttentionInput): string | null {
  if ((input.consecutiveMissed ?? 0) >= 2) return `${input.consecutiveMissed} missed in a row`;
  if (!input.hasProgram) return 'No program assigned';
  if (typeof input.complianceRate === 'number' && input.complianceRate < 50) {
    return `${input.complianceRate}% adherence`;
  }
  return null;
}

/** Whole days from `fromIso` to `toIso` (both YYYY-MM-DD). */
export function daysBetween(fromIso: string, toIso: string): number {
  const from = Date.UTC(...isoParts(fromIso));
  const to = Date.UTC(...isoParts(toIso));
  return Math.round((to - from) / 86_400_000);
}

function isoParts(iso: string): [number, number, number] {
  const [year = 1970, month = 1, day = 1] = iso.slice(0, 10).split('-').map(Number);
  return [year, month - 1, day];
}

export type ClientStatus =
  | { kind: 'attention'; reason: string }
  | { kind: 'on-track' }
  | { kind: 'unknown'; reason: string };

/**
 * List and detail status: the three Needs attention triggers first, then an unknown
 * missed streak or unreadable adherence never passes as on track.
 */
export function clientStatus(input: ClientAttentionInput & { adherenceUnknown: boolean }): ClientStatus {
  const reason = clientAttentionReason(input);
  if (reason) return { kind: 'attention', reason };
  if (input.consecutiveMissed === null) {
    return { kind: 'unknown', reason: 'Missed workouts could not be loaded' };
  }
  if (input.adherenceUnknown) {
    return { kind: 'unknown', reason: 'Adherence could not be loaded' };
  }
  return { kind: 'on-track' };
}

/** Weeks left in the program after the current one; null without a program. */
export function programWeeksLeft(program: ActiveProgram): number | null {
  if (!program || program.duration_weeks <= 0) return null;
  return Math.max(0, program.duration_weeks - program.current_week);
}

export type ClientSignalTone = 'alert' | 'voice' | 'info' | 'win';

export interface ClientSignal {
  key: 'inactive' | 'missed' | 'adherence' | 'brutal' | 'program-ending' | 'form-checks' | 'best-lift' | 'streak';
  tone: ClientSignalTone;
  title: string;
  detail: string | null;
}

export interface ClientSignalInput {
  /** The client's product date; null skips date-based signals. */
  todayIso: string | null;
  activeProgram: ActiveProgram;
  recentWorkouts: RecentWorkout[];
  complianceRate: number | null;
  consecutiveMissed: number | null;
  /** Latest completed workout over all history; null when none or unknown. */
  lastCompletedWorkout: TrainerClientDetails['last_completed_workout'];
  /** Null while progress is loading or failed: progress-based signals are skipped. */
  snapshot: ProgressSnapshot | null | undefined;
  /** All-time bests for the top lifts; null when unknown, which skips the best-lift signal. */
  liftRecords: LiftRecord[] | null | undefined;
  /** Null while form checks are loading or failed. */
  formChecksToReview: number | null;
}

export function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/** Signals in display order: alerts, the client's own words, follow-ups, then wins. */
export function buildClientSignals(input: ClientSignalInput): ClientSignal[] {
  const signals: ClientSignal[] = [];

  const last = input.lastCompletedWorkout;
  if (last && input.todayIso) {
    const days = daysBetween(last.date_iso, input.todayIso);
    if (days >= INACTIVE_DAYS) {
      signals.push({
        key: 'inactive',
        tone: 'alert',
        title: `${days} days since last workout`,
        detail: `Last: ${last.name}`,
      });
    }
  }

  if ((input.consecutiveMissed ?? 0) >= 2) {
    // The streak spans every program, so the active program's rows cannot name it.
    signals.push({
      key: 'missed',
      tone: 'alert',
      title: `${input.consecutiveMissed} missed in a row`,
      detail: 'Most recent scheduled workouts, across programs',
    });
  }

  if (typeof input.complianceRate === 'number' && input.complianceRate < 50) {
    signals.push({
      key: 'adherence',
      tone: 'alert',
      title: `${input.complianceRate}% adherence`,
      detail: 'Completed vs scheduled over the last 14 days',
    });
  }

  const brutal = input.recentWorkouts.find(
    (workout) => workout.status === 'completed' && workout.difficulty === 'Brutal',
  );
  if (brutal) {
    signals.push({
      key: 'brutal',
      tone: 'voice',
      title: `Rated ${brutal.name} Brutal`,
      detail: brutal.feedback_text?.trim() ? `“${brutal.feedback_text.trim()}”` : null,
    });
  }

  const weeksLeft = programWeeksLeft(input.activeProgram);
  if (weeksLeft !== null && weeksLeft <= 2) {
    signals.push({
      key: 'program-ending',
      tone: 'info',
      title: weeksLeft === 0 ? 'Final program week' : `${plural(weeksLeft, 'week')} left after this one`,
      detail: 'Plan the next block before it runs out',
    });
  }

  if ((input.formChecksToReview ?? 0) > 0) {
    signals.push({
      key: 'form-checks',
      tone: 'info',
      title: `${plural(input.formChecksToReview ?? 0, 'form check')} to review`,
      detail: 'Sent after a finished workout',
    });
  }

  const recordByLift = new Map((input.liftRecords ?? []).map((record) => [record.exercise_library_id, record.max_weight_kg]));
  const atBest = input.liftRecords
    ? (input.snapshot?.top_lifts ?? []).flatMap((lift) => {
        const latest = lift.trend.at(-1);
        const record = recordByLift.get(lift.exercise_library_id);
        return latest !== undefined && record !== undefined && latest.weight_kg >= record
          ? [`${lift.exercise_name} ${record} kg`]
          : [];
      })
    : [];
  if (atBest.length > 0) {
    signals.push({
      key: 'best-lift',
      tone: 'win',
      title: `At best on record: ${atBest.join(', ')}`,
      detail: 'Latest set matches their heaviest logged',
    });
  }

  const stats = input.snapshot?.stats;
  if (stats && stats.current_streak >= 3) {
    signals.push({
      key: 'streak',
      tone: 'win',
      title: `${stats.current_streak}-day streak`,
      detail: `Best so far: ${plural(stats.longest_streak, 'day')}`,
    });
  }

  return signals;
}
